import { Injectable } from '@angular/core';
import {LocalStorage} from '@efaps/ngx-store';
import {BehaviorSubject, Subscription} from 'rxjs';
import {AngularFirestore, AngularFirestoreCollection} from '@angular/fire/compat/firestore';
import {UserService} from './user.service';
import {
  ExtensionActivity,
  ExtensionEvent,
  Hort360GrowerProperty,
  Hort360PropertySummary
} from '../../../functions/src/types';
import firebase from 'firebase/compat/app';

@Injectable({
  providedIn: 'root'
})
export class PropertyService {
  private MAX_RECENT_VALUES = 20;
  public recent: (number | string)[] = [];
  public recentInfo: BehaviorSubject<Hort360PropertySummary[]> = new BehaviorSubject<Hort360PropertySummary[]>([]);
  public recentIds: BehaviorSubject<(number | string)[]> = new BehaviorSubject<(number | string)[]>([]);

  public summaryPropertiesRef: AngularFirestoreCollection<Hort360PropertySummary>;
  public summaryContactsRef: AngularFirestoreCollection<Hort360PropertySummary>;
  public summaryProperties: {[key: number]: Hort360PropertySummary} = {};
  public summaryContacts: {[key: number]: Hort360PropertySummary} = {};
  public summaryPropertiesChanged = new BehaviorSubject<{[key: number]: Hort360PropertySummary}>({});
  public summaryContactsChanged = new BehaviorSubject<{[key: number]: Hort360PropertySummary}>({});

  public initialLoad: Promise<boolean> | null;
  private initialLoadResolver: (value?: (PromiseLike<boolean> | boolean)) => void;

  private propertiesCollection: AngularFirestoreCollection<Hort360GrowerProperty>;
  private watchers: Subscription[] = [];
  private recentCollection: AngularFirestoreCollection<UserRecentProperty>;

  constructor(private fbDb: AngularFirestore, private userService: UserService) {
    this.initialLoad = new Promise(resolve => {
      this.initialLoadResolver = resolve;
    });

    //
    this.summaryPropertiesRef = this.fbDb.collection<Hort360PropertySummary>('propertySummary');
    this.summaryContactsRef = this.fbDb.collection<Hort360PropertySummary>('contactSummary');

    this.userService.activeUserState.subscribe((aus) => {

      // Tidy up;
      this.watchers.forEach(w => w.unsubscribe());
      this.watchers = [];
      this.summaryProperties = {};
      this.summaryContacts = {};
      this.recentInfo.next([]);
      this.recentIds.next([]);
      this.summaryPropertiesChanged.next({});
      this.summaryContactsChanged.next({});
      this.recentCollection = null;

      // Watch for changes
      if (aus) {
        // We're logged in, so get property summaries for local display
        let loading = 2;
        this.watchers.push(
          this.summaryPropertiesRef.valueChanges({idField: 'id'}).subscribe(users => {
              // this.summaryProperties = users;
              this.summaryProperties = {};
              users.forEach(u => this.summaryProperties[u.id] = u);
              loading--;
              if (loading === 0 && this.initialLoadResolver) {
                this.initialLoadResolver();
                this.initialLoad = null;
                this.initialLoadResolver = null;
              }
              this.summaryPropertiesChanged.next(this.summaryProperties);
            }
          ),
          this.summaryContactsRef.valueChanges({idField: 'id'}).subscribe(users => {
              // this.summaryProperties = users;
              this.summaryContacts = {};
              users.forEach(u => {
                this.summaryContacts[u.id] = u;
                this.summaryContacts[u.id].managerOnly = true;
              });
              loading--;
              if (loading === 0 && this.initialLoadResolver) {
                this.initialLoadResolver();
                this.initialLoad = null;
                this.initialLoadResolver = null;
              }
              console.log(this.summaryContacts);
              this.summaryContactsChanged.next(this.summaryContacts);
            }
          )
        );

        // Watch the recent values
        this.recentCollection = this.fbDb.collection<UserRecentProperty>(
          `users/${this.userService.activeUserId}/recents`,
          ref => ref.orderBy('ts', 'desc')
        );
        this.watchers.push(
          this.recentCollection.valueChanges({idField: 'id'}).subscribe(values => {
            this.recent = values.map(v => v.id + '');
            this.recentBroadcast();
          })
        );
      }
    });

    // Ref
    this.propertiesCollection = this.fbDb.collection<Hort360GrowerProperty>('properties');
  }

  public getProperty(id: number | string): [Hort360PropertySummary, Promise<Hort360GrowerProperty>] {
    const summaryProperty = this.getPropertySummary(id);
    return [
      summaryProperty,
      new Promise<Hort360GrowerProperty>(async (resolve, reject) => {
        if (!summaryProperty) {
          reject('Something went wrong. Property does not exist.');
        } else if (!summaryProperty.managerOnly) {
          try {
            const doc = await this.propertiesCollection.doc(id + '').get().toPromise();
            if (doc.exists) {
              const data = doc.data();
              resolve(data as Hort360GrowerProperty);
              return;
            }
            reject('Something went wrong. Property does not exist.');
          } catch (e) {
            console.error(e);
            reject('Something went wrong. ' + e.message);
          }
        } else {
          // Manager only!
          const psudoProperty = {
            id: summaryProperty.id,
            propertyId: id,
            growerId: null,
            propertyName: summaryProperty.name,
            growerName: summaryProperty.growerDescription,
            managerOnly: true
          } as Hort360GrowerProperty;
          resolve(psudoProperty);
        }
      })
    ];
  }
  public getPropertySummary(id: number | string): Hort360PropertySummary {
    return this.summaryProperties[id] || this.summaryProperties[id + ''] ||
           this.summaryContacts[id] || this.summaryContacts[id + ''] || null;
  }
  public getPropertySummaries(ids: any[]): Hort360PropertySummary[] {
    return ids.map(id => this.getPropertySummary(id) || null).filter(p => !!p);
  }

  public getPropertyReefCertifiedRef(id: number | string): AngularFirestoreCollection<any[]> {
    return this.propertiesCollection.doc(id + '').collection('reefcertified', ref => ref.orderBy('date', 'asc'));
  }

  private recentBroadcast() {
    this.recentIds.next(this.recent);

    // Get the recent details;
    const recentSummaries = this.getPropertySummaries(this.recent);
    this.recentInfo.next(recentSummaries);
  }
  async addRecent(propertyId: number | string) {
    const batch = this.fbDb.firestore.batch();
    try {
      if (this.recent && this.recent.length === this.MAX_RECENT_VALUES) {
        // Delete the oldest;
        const oldestRef = this.recentCollection.doc(this.recent[this.recent.length - 1] + '').ref;
        batch.delete(oldestRef as any);
      }
      const ts: any = firebase.firestore.FieldValue.serverTimestamp();
      batch.set(this.recentCollection.doc(propertyId + '').ref as any, { ts });

      // Commit;
      await batch.commit();

    } catch (e) {
      console.error(e);
    }
  }
  async removeRecent(propertyId: number) {
    try {
      const ts = firebase.firestore.FieldValue.serverTimestamp();
      await this.recentCollection.doc(propertyId + '').delete();
    } catch (e) {
      console.error(e);
    }
  }

}

interface UserRecentProperty {
  id: number;
  ts: number;
}

